import { isEmpty } from "lodash";
import React, { useState, useCallback } from "react";
import {
  SideNavStateProvider,
  SideNavV2,
  Popover,
  Avatar,
  Flex,
  Button,
  Icon,
  Divider,
  H6,
  Badge,
} from "orcs-design-system";
import { Link, useLocation } from "react-router-dom";
import styled, { createGlobalStyle } from "styled-components";
import themeGet from "@styled-system/theme-get";

import useSideNavClickListener from "src/hooks/useSideNavClickListener";
import {
  useWorkspaceTagConfig,
  useGroupTypes,
} from "src/contexts/global/WorkspaceContext";
import { useAuth0 } from "src/contexts/auth0Provider";
import { useFeatureFlags } from "src/contexts/global/FeatureFlagsContext";
import useSideNavItemBuilder from "src/sidebar/useSideNavItemBuilder";
import eventEmitter from "src/services/eventEmitter";
import { usePinnedEntities } from "src/hooks/usePinnedEntities";
import useUserTeams from "src/components/SideNav/useUserTeams";
import { useUserSideNav } from "src/hooks/userPreferences/hooks/useUserSideNav";
import { useAppHeadersHeightCalc } from "src/hooks/useAppHeadersHeightCalc";
import { getGroupTypeBadgeVariant } from "src/util/group";
import { getGroupType } from "src/util/groupType";
import GroupTypeBadge from "src/components/Badge/GroupTypeBadge";
import useFilterNavItems from "./hooks/useFilterNavItems";
import TeamTypeBar from "./components/TeamTypeBar";

// Global style to align people and tags items with teams indentation
const SideNavAlignmentFix = createGlobalStyle`
  /* Match padding with teams section */
  [data-testid="side-nav-items"] [class*="SideNavItem"] > a,
  [data-testid="side-nav-items"] [class*="SideNavItem"] {
    padding: ${(props) => themeGet("space.xs")(props)} !important;
  }

  /* Ensure pinned items text aligns with teams - use data attribute for reliable targeting */
  a[data-pinned-link="true"] {
    padding: ${(props) => themeGet("space.xs")(props)} !important;
    gap: ${(props) => themeGet("space.xs")(props)} !important;
  }

  /* Make Popover container fill space for full row clickability */
  a[data-pinned-link="true"] > div {
    width: 100%;
    height: 100%;
    min-height: 100%;
  }

  /* Prevent grey artifacts at bottom of collapsed sidenav */
  [data-testid="side-nav-items"] {
    overflow: hidden;
  }

  /* Hide any empty items that might cause artifacts */
  [data-testid="side-nav-items"] > *:empty {
    display: none !important;
  }
`;

// Styled components for team type indicators
const ItemList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  align-items: ${(props) => (props.isExpanded ? "flex-start" : "center")};
  text-align: ${(props) => (props.isExpanded ? "left" : "center")};
  display: flex;
  flex-direction: column;
  gap: ${(props) => themeGet("space.xs")(props)};

  /* Remove any bottom margin/padding that could cause artifacts */
  &:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
  }

  /* Hide empty items */
  > li:empty {
    display: none;
  }
`;

const Item = styled.li`
  display: flex;
  width: 100%;
  gap: ${(props) => themeGet("space.xs")(props)};
  justify-content: ${(props) => {
    if (props.isExpanded) {
      return "space-between";
    }
    return "center";
  }};
  align-items: stretch;
  padding: 0;
  border-radius: ${(props) => themeGet("radii.2")(props)};
  transition: ${(props) => themeGet("transition.transitionDefault")(props)};
  min-height: 0;
  position: relative;

  &.active {
    background-color: ${(props) => themeGet("colors.primaryLightest")(props)};
  }

  &:hover {
    background-color: ${(props) =>
      props.isExpanded
        ? themeGet("colors.greyLightest")(props)
        : "transparent"};
  }

  /* Make PinnedLink text blue when hovering over Item */
  &:hover a {
    color: ${(props) => themeGet("colors.primary")(props)};
  }

  &.active:hover {
    background-color: ${(props) => themeGet("colors.primaryLightest")(props)};
  }

  /* Prevent hover state on empty items */
  &:empty {
    display: none;
  }

  /* Ensure PinnedLink and TeamLink have consistent spacing */
  a[data-pinned-link="true"],
  a[data-team-link="true"] {
    flex: 1;
    width: 100%;
  }
`;

const TeamLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: ${(props) => themeGet("space.xs")(props)};
  font-size: ${(props) => themeGet("fontSizes.1")(props)};
  text-decoration: none;
  color: ${(props) => themeGet("colors.greyDarkest")(props)};
  padding: ${(props) => themeGet("space.xs")(props)};
  border-radius: ${(props) => themeGet("radii.2")(props)};
  flex: 1;
  width: 100%;
  transition: ${(props) => themeGet("transition.transitionDefault")(props)};

  /* Ensure avatar/icon container has consistent width for alignment */
  > *:first-child {
    flex-shrink: 0;
    width: 24px;
    height: 24px;
  }

  &:hover,
  &:focus {
    color: ${(props) => themeGet("colors.primary")(props)};
  }

  &:focus-visible {
    outline: ${(props) => themeGet("space.xxs")(props)} solid
      ${(props) => themeGet("colors.primary")(props)};
    outline-offset: ${(props) => themeGet("space.xxs")(props)};
  }
`;

const PinnedLink = styled(Link)`
  display: flex;
  align-items: center;
  font-size: ${(props) => themeGet("fontSizes.1")(props)};
  text-decoration: none;
  color: ${(props) => themeGet("colors.greyDarkest")(props)};
  padding: ${(props) => themeGet("space.xs")(props)};
  border-radius: ${(props) => themeGet("radii.2")(props)};
  flex: 1;
  width: 100%;
  min-height: 100%;
  align-self: stretch;
  transition: ${(props) => themeGet("transition.transitionDefault")(props)};

  &:hover,
  &:focus {
    color: ${(props) => themeGet("colors.primary")(props)};
  }

  &:focus-visible {
    outline: ${(props) => themeGet("space.xxs")(props)} solid
      ${(props) => themeGet("colors.primary")(props)};
    outline-offset: ${(props) => themeGet("space.xxs")(props)};
  }
`;

const PinnedLinkContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) => themeGet("space.xs")(props)};
  width: 100%;
  min-height: 100%;

  /* Add padding-left to text spans for people and tags only, not teams */
  /* Teams should match the indentation from "My Teams" section */
  > span:last-child {
    padding-left: ${(props) =>
      props.isTeam ? "0" : themeGet("space.s")(props)};
  }
`;

const CollapsedItemWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${(props) => themeGet("space.xs")(props)};
  border-radius: ${(props) => themeGet("radii.2")(props)};
  transition: ${(props) => themeGet("transition.transitionDefault")(props)};
  cursor: pointer;
  gap: ${(props) => themeGet("space.xxs")(props)};

  &.active {
    background-color: ${(props) => themeGet("colors.primaryLightest")(props)};
  }

  &:hover:not(.active) {
    background-color: ${(props) => themeGet("colors.greyLightest")(props)};
  }
`;

const CollapsedPinnedWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${(props) => themeGet("space.xs")(props)};
  border-radius: ${(props) => themeGet("radii.2")(props)};
  transition: ${(props) => themeGet("transition.transitionDefault")(props)};
  cursor: pointer;
  gap: ${(props) => themeGet("space.xxs")(props)};

  &.active {
    background-color: ${(props) => themeGet("colors.primaryLightest")(props)};
  }

  &:hover:not(.active) {
    background-color: ${(props) => themeGet("colors.greyLightest")(props)};
  }
`;

const AvatarWrapper = styled.div`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CollapsedLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: ${(props) => themeGet("space.xxs")(props)};
  text-decoration: none;
  color: inherit;
`;

const SmallBadge = styled(Badge)`
  flex-shrink: 0;
  font-size: ${(props) => themeGet("fontSizes.0")(props)};
  padding: ${(props) => themeGet("space.xxs")(props)}
    ${(props) => themeGet("space.xs")(props)};
  line-height: 1.2;
`;

const SmallGroupTypeBadge = styled(GroupTypeBadge)`
  flex-shrink: 0;
  font-size: ${(props) => themeGet("fontSizes.0")(props)};
  padding: ${(props) => themeGet("space.xxs")(props)}
    ${(props) => themeGet("space.xs")(props)};
  line-height: 1.2;
`;

const PinnedIconWrapper = styled.div`
  position: absolute;
  top: 50%;
  right: ${(props) => themeGet("space.xs")(props)};
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 0;
  z-index: 10;
  flex-shrink: 0;
`;

const NovaSideNav = () => {
  const [clickedItem, setClickedItem] = useState({});
  const tagTypesConfig = useWorkspaceTagConfig();
  const { user } = useAuth0();
  const featureFlags = useFeatureFlags();
  const { userTeams } = useUserTeams();
  const { pinnedEntities } = usePinnedEntities();
  const { sideNav, setKeepExpanded } = useUserSideNav();
  const groupTypes = useGroupTypes() || {};
  const location = useLocation();
  const sideNavHeight = useAppHeadersHeightCalc({
    includeProfileHeaders: false,
    hideHeader: false,
  });

  const navItems = useSideNavItemBuilder({
    clickedItem,
    user,
    tagTypesConfig,
    featureFlags,
    eventEmitter,
    setClickedItem,
  });

  useSideNavClickListener();
  const filteredNavItems = useFilterNavItems(navItems);

  const handleKeepExpandedChange = useCallback(
    (keepExpanded) => {
      setKeepExpanded(keepExpanded);
    },
    [setKeepExpanded]
  );

  if (isEmpty(filteredNavItems)) {
    return null;
  }

  return (
    <SideNavStateProvider>
      <SideNavAlignmentFix />
      <SideNavV2
        items={filteredNavItems}
        sideNavHeight={sideNavHeight}
        yourTeams={userTeams}
        pinnedItems={pinnedEntities}
        startExpanded={sideNav?.keepExpanded}
        onKeepExpandedChange={handleKeepExpandedChange}
        renderTeamsSection={({ teams, isExpanded }) => {
          if (!teams || teams.length === 0) {
            return null;
          }

          const renderTeamItem = (team) => {
            const { avatar, name, link, gradient, type, id } = team;
            if (!name || !link) {
              return null;
            }

            const groupObject = { type, id, name };
            const hasTeamType = Boolean(type);
            const groupType = hasTeamType
              ? getGroupType(groupObject, groupTypes)
              : null;
            const badgeVariant = groupType
              ? getGroupTypeBadgeVariant(groupType)
              : "default";
            const isActive = link && location.pathname.startsWith(link);

            // Same popover content for both collapsed and expanded states
            const teamPopoverContent = hasTeamType ? (
              <Flex
                flexDirection="row"
                flexWrap="wrap"
                gap="xs"
                alignItems="center"
              >
                <span>{name}</span>
                <SmallGroupTypeBadge
                  group={groupObject}
                  groupTypes={groupTypes}
                />
              </Flex>
            ) : (
              <span>{name}</span>
            );

            if (isExpanded) {
              return (
                <Item
                  key={id || name}
                  isExpanded={isExpanded}
                  className={isActive ? "active" : ""}
                >
                  <Popover
                    text={teamPopoverContent}
                    direction="right"
                    width="fit-content"
                    tabIndex="-1"
                    inlineBlock={false}
                    style={{ width: "100%", display: "flex", flex: 1 }}
                  >
                    <TeamLink to={link} aria-label={name} data-team-link="true">
                      <AvatarWrapper>
                        <Avatar
                          image={avatar}
                          customSize="24px"
                          shape="square"
                          gradient={gradient}
                        />
                      </AvatarWrapper>
                      {hasTeamType && (
                        <TeamTypeBar badgeVariant={badgeVariant} />
                      )}
                      <span>{name}</span>
                    </TeamLink>
                  </Popover>
                </Item>
              );
            }

            return (
              <Item key={id || name} isExpanded={isExpanded}>
                <Popover
                  text={teamPopoverContent}
                  direction="right"
                  width="fit-content"
                  tabIndex="-1"
                >
                  <CollapsedItemWrapper className={isActive ? "active" : ""}>
                    <CollapsedLink to={link} aria-label={name}>
                      <AvatarWrapper>
                        <Avatar
                          image={avatar}
                          customSize="24px"
                          shape="square"
                          gradient={gradient}
                        />
                      </AvatarWrapper>
                      {hasTeamType && (
                        <TeamTypeBar badgeVariant={badgeVariant} />
                      )}
                    </CollapsedLink>
                  </CollapsedItemWrapper>
                </Popover>
              </Item>
            );
          };

          return (
            <>
              <Divider light display={["none", "none", "none", "block"]} />
              <Flex
                flexDirection="column"
                gap="r"
                my={isExpanded ? "s" : "0"}
                display={["none", "none", "none", "flex"]}
              >
                {isExpanded && (
                  <H6 sizing="small" color="greyDark">
                    My Teams
                  </H6>
                )}
                <ItemList isExpanded={isExpanded}>
                  {teams.map(renderTeamItem)}
                </ItemList>
              </Flex>
            </>
          );
        }}
        renderPinnedSection={({ items: pinnedItems, isExpanded }) => {
          if (!pinnedItems || pinnedItems.length === 0) {
            return null;
          }

          const renderPinnedItem = (item) => {
            const {
              avatar,
              name,
              link,
              shape,
              onUnpin,
              gradient,
              type,
              entityType,
              id,
              jobTitle,
              tagTypeName,
            } = item;
            if (!name || !link) {
              return null;
            }

            const showTeamType = entityType === "team" && type;
            const groupObject = showTeamType ? { type, id, name } : null;
            const groupType = showTeamType
              ? getGroupType(groupObject, groupTypes)
              : null;
            const badgeVariant = groupType
              ? getGroupTypeBadgeVariant(groupType)
              : "default";
            const isActive = link && location.pathname.startsWith(link);

            // Same popover content for both collapsed and expanded states
            const getPopoverContent = () => {
              if (entityType === "team" && showTeamType) {
                return (
                  <Flex
                    flexDirection="row"
                    flexWrap="wrap"
                    gap="xs"
                    alignItems="center"
                  >
                    <span>{name}</span>
                    <SmallGroupTypeBadge
                      group={groupObject}
                      groupTypes={groupTypes}
                    />
                  </Flex>
                );
              }

              if (entityType === "person") {
                return (
                  <Flex
                    flexDirection="row"
                    flexWrap="wrap"
                    gap="xs"
                    alignItems="center"
                  >
                    <span>{name}</span>
                    {jobTitle && <SmallBadge>{jobTitle}</SmallBadge>}
                  </Flex>
                );
              }

              if (entityType === "tag") {
                return (
                  <Flex
                    flexDirection="row"
                    flexWrap="wrap"
                    gap="xs"
                    alignItems="center"
                  >
                    <span>{name}</span>
                    {tagTypeName && <SmallBadge>{tagTypeName}</SmallBadge>}
                  </Flex>
                );
              }

              return <span>{name}</span>;
            };

            if (isExpanded) {
              return (
                <Item
                  key={id || name}
                  isExpanded={isExpanded}
                  className={isActive ? "active" : ""}
                >
                  <PinnedLink
                    to={link}
                    aria-label={name}
                    data-pinned-link="true"
                  >
                    <Popover
                      text={getPopoverContent()}
                      direction="right"
                      width="fit-content"
                      tabIndex="-1"
                      inlineBlock={false}
                      style={{ width: "100%", display: "flex", flex: 1 }}
                    >
                      <PinnedLinkContent isTeam={entityType === "team"}>
                        <AvatarWrapper>
                          <Avatar
                            image={avatar}
                            customSize="24px"
                            shape={shape}
                            gradient={gradient}
                          />
                        </AvatarWrapper>
                        {showTeamType && (
                          <TeamTypeBar badgeVariant={badgeVariant} />
                        )}
                        <span>{name}</span>
                      </PinnedLinkContent>
                    </Popover>
                  </PinnedLink>
                  {onUnpin && (
                    <PinnedIconWrapper>
                      <Popover
                        text="Unpin item"
                        direction="right"
                        width="fit-content"
                        tabIndex="-1"
                      >
                        <Button
                          height="20px"
                          width="20px"
                          borderRadius="20px"
                          variant="borderGrey"
                          borderColor="white"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            onUnpin(item);
                          }}
                          type="button"
                        >
                          <Icon
                            icon={["fas", "thumbtack"]}
                            color="greyDarkest"
                            size="sm"
                          />
                        </Button>
                      </Popover>
                    </PinnedIconWrapper>
                  )}
                </Item>
              );
            }

            return (
              <Item key={id || name} isExpanded={isExpanded}>
                <Popover
                  text={getPopoverContent()}
                  direction="right"
                  width="fit-content"
                  tabIndex="-1"
                >
                  <CollapsedPinnedWrapper className={isActive ? "active" : ""}>
                    <CollapsedLink to={link} aria-label={name}>
                      <AvatarWrapper>
                        <Avatar
                          image={avatar}
                          customSize="24px"
                          shape={shape}
                          gradient={gradient}
                        />
                      </AvatarWrapper>
                      {showTeamType && (
                        <TeamTypeBar badgeVariant={badgeVariant} />
                      )}
                    </CollapsedLink>
                  </CollapsedPinnedWrapper>
                </Popover>
              </Item>
            );
          };

          return (
            <>
              <Divider light display={["none", "none", "none", "block"]} />
              <Flex
                flexDirection="column"
                gap="r"
                my={isExpanded ? "s" : "0"}
                display={["none", "none", "none", "flex"]}
                mb="0"
                pb="0"
              >
                {isExpanded && (
                  <H6 sizing="small" color="greyDark">
                    Pinned
                  </H6>
                )}
                <ItemList isExpanded={isExpanded}>
                  {pinnedItems.map(renderPinnedItem)}
                </ItemList>
              </Flex>
            </>
          );
        }}
      />
    </SideNavStateProvider>
  );
};

export default NovaSideNav;
